import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

interface Point {
  x: number;
  y: number;
}

@Component({
  selector: 'lib-sign-box',
  imports: [CommonModule, TranslateModule],
  templateUrl: './sign-box.component.html',
  styleUrl: './sign-box.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignBoxComponent {
  @Input() public isDisabled: boolean = false;

  @Output() public isSigned = new EventEmitter<boolean>();
  @Output() public blobCreated = new EventEmitter<Blob | null>();

  @ViewChild('canvas', { static: true })
  protected canvasRef!: ElementRef<HTMLCanvasElement>;

  protected drawingPoints: Point[][] = [];
  #drawing: boolean = false;

  public onClearClick(): void {
    this.drawingPoints = [];
    this.draw();
    this.isSigned.emit(false);
  }

  public onMouseDownOnCanvas(event: MouseEvent): void {
    this.drawingPoints.push([{ x: event.offsetX, y: event.offsetY }]);

    this.#drawing = true;
  }

  public onMouseMoveOnCanvas(event: MouseEvent): void {
    if (!this.#drawing) {
      return;
    }

    const mouseCurrentX = event.offsetX;
    const mouseCurrentY = event.offsetY;

    const points = this.drawingPoints;
    points.at(-1)!.push({ x: mouseCurrentX, y: mouseCurrentY });

    this.draw();
  }

  public onMouseUpOnCanvas(_event: MouseEvent): void {
    this.#drawing = false;
    this.isSigned.emit(true);
    this.canvasRef.nativeElement.toBlob((blob) => this.blobCreated.emit(blob));
  }

  public onMouseLeaveCanvas(_event: MouseEvent): void {
    if (this.#drawing) {
      this.onMouseUpOnCanvas(_event);
    }
  }

  private draw() {
    requestAnimationFrame(() => {
      const canvas = this.canvasRef.nativeElement;
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;

      const context = canvas.getContext('2d');

      if (!context) {
        return;
      }

      context.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);

      context.strokeStyle = 'rgb(0,0,0)';
      context.lineWidth = 2;
      context.globalAlpha = 1;
      context.lineCap = 'round';
      context.lineJoin = 'round';

      context.beginPath();
      this.drawingPoints.forEach((points) => {
        context.moveTo(points[0].x, points[0].y);
        points.forEach((point) => {
          context.lineTo(point.x, point.y);
        });
      });
      context.stroke();
    });
  }
}
